import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { api } from "@/lib/api";
import type { DailyLog as DailyLogType } from "@/lib/types";

const empty: DailyLogType = {
  date: "",
  thyronorm_gap_min: null,
  sleep_hours: null,
  wake_time: null,
  protein_g: null,
  steps: null,
  run_km: null,
  lift_session: null,
  screen_time_min: null,
  learning_min: null,
  claude_pre_notes: null,
  mood: null,
  note: null,
};

export function DailyLog() {
  const [params] = useSearchParams();
  const date = params.get("date") ?? format(new Date(), "yyyy-MM-dd");
  const [form, setForm] = useState<DailyLogType>({ ...empty, date });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.dailyGet(date).then((d) => {
      setForm({ ...empty, date, ...(d ?? {}) });
      setLoading(false);
    });
  }, [date]);

  function set<K extends keyof DailyLogType>(k: K, v: DailyLogType[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  }

  function num(v: string): number | null {
    if (v === "") return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.dailyUpsert(form);
      setSaved(true);
    } catch (err) {
      alert(String(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Daily log</h1>
        <p className="text-sm text-muted">60 seconds. Log before you sleep.</p>
      </div>

      <div className="card grid grid-cols-2 gap-3">
        <div>
          <label className="label">Date</label>
          <input type="date" className="field" value={form.date}
            onChange={(e) => set("date", e.target.value)} />
        </div>
        <div>
          <label className="label">Wake time</label>
          <input type="time" className="field" value={form.wake_time ?? ""}
            onChange={(e) => set("wake_time", e.target.value || null)} />
        </div>
      </div>

      <section className="card space-y-3">
        <h2 className="text-sm font-semibold text-accent uppercase tracking-wider">Biology</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Thyronorm → food gap (min) <span className="text-accent">★</span></label>
            <input type="number" inputMode="numeric" className="field" placeholder="target 75"
              value={form.thyronorm_gap_min ?? ""}
              onChange={(e) => set("thyronorm_gap_min", num(e.target.value))} />
          </div>
          <div>
            <label className="label">Sleep (hours)</label>
            <input type="number" inputMode="decimal" step="0.25" className="field" placeholder="target 7.5"
              value={form.sleep_hours ?? ""}
              onChange={(e) => set("sleep_hours", num(e.target.value))} />
          </div>
        </div>
      </section>

      <section className="card space-y-3">
        <h2 className="text-sm font-semibold text-accent uppercase tracking-wider">Body</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Protein (g)</label>
            <input type="number" inputMode="numeric" className="field" placeholder="target 130"
              value={form.protein_g ?? ""}
              onChange={(e) => set("protein_g", num(e.target.value))} />
          </div>
          <div>
            <label className="label">Steps</label>
            <input type="number" inputMode="numeric" className="field" placeholder="target 8000"
              value={form.steps ?? ""}
              onChange={(e) => set("steps", num(e.target.value))} />
          </div>
          <div>
            <label className="label">Run (km)</label>
            <input type="number" inputMode="decimal" step="0.1" className="field"
              value={form.run_km ?? ""}
              onChange={(e) => set("run_km", num(e.target.value))} />
          </div>
          <div>
            <label className="label">Lift session</label>
            <select className="field"
              value={form.lift_session ?? ""}
              onChange={(e) => set("lift_session", e.target.value || null)}>
              <option value="">—</option>
              <option value="lower">Lower</option>
              <option value="push">Push</option>
              <option value="pull">Pull</option>
              <option value="cardio_only">Cardio only</option>
              <option value="rest">Rest</option>
            </select>
          </div>
        </div>
      </section>

      <section className="card space-y-3">
        <h2 className="text-sm font-semibold text-accent uppercase tracking-wider">Mind</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Screen time (min)</label>
            <input type="number" inputMode="numeric" className="field" placeholder="target <240"
              value={form.screen_time_min ?? ""}
              onChange={(e) => set("screen_time_min", num(e.target.value))} />
          </div>
          <div>
            <label className="label">Learning block (min)</label>
            <input type="number" inputMode="numeric" className="field" placeholder="target 60"
              value={form.learning_min ?? ""}
              onChange={(e) => set("learning_min", num(e.target.value))} />
          </div>
          <div className="col-span-2">
            <label className="label">Wrote 3 bullets before Claude Code? <span className="text-accent">★</span></label>
            <div className="flex gap-2">
              {[{ v: 1, t: "Yes" }, { v: 0, t: "No" }, { v: null, t: "—" }].map((o) => (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => set("claude_pre_notes", o.v)}
                  className={`flex-1 py-2 rounded-lg border ${
                    form.claude_pre_notes === o.v
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-border text-muted"
                  }`}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <label className="label">Mood (1–10)</label>
            <input type="range" min={1} max={10} className="w-full accent-emerald-500"
              value={form.mood ?? 5}
              onChange={(e) => set("mood", num(e.target.value))} />
            <div className="text-center text-accent font-mono">{form.mood ?? "—"}</div>
          </div>
        </div>
      </section>

      <section className="card">
        <label className="label">One-line note</label>
        <input className="field" placeholder="e.g. skipped breakfast, great long run, anxious meeting"
          value={form.note ?? ""}
          onChange={(e) => set("note", e.target.value || null)} />
      </section>

      <div className="sticky bottom-4">
        <button type="submit" disabled={saving} className="btn-primary w-full">
          {saving ? "Saving…" : saved ? "✓ Saved" : "Save"}
        </button>
      </div>
    </form>
  );
}
